import {
  SIP_ORDER_LINK_ACTION,
  TRACKING_FUNCTION_CONNECT_SIP_ORDER,
  TRACKING_PROPERTY_ACTION,
  TrackingEntry,
} from '../../../core/services/tracking-api.model';

/** Kind of a row in the SIP order link history. */
export type SipOrderLinkRowKind = 'sent' | 'opened' | 'ordered';

/**
 * One row of the SIP order link history. Repeated LINK_OPENED entries of the same link are
 * collapsed into one `opened` row (`openCount`, `at` = the last opening): every offer call with a
 * valid token is tracked, so a customer reloading the page would otherwise flood the list.
 *
 * @ticket PS-6586
 */
export interface SipOrderLinkRow {
  kind: SipOrderLinkRowKind;
  /** When it happened (tracking `start`, local time); for `opened` the most recent opening. */
  at: Date;
  linkId: string | null;
  /** `sent` only: employee user account id that sent the link. */
  issuedBy: number | null;
  /** `sent` only: when the link stops working. */
  expiresAt: Date | null;
  /** `opened` only: how often the link was opened. */
  openCount: number;
  /** `ordered` only: `ORDERED` or `ALREADY_EXISTS`. */
  result: string | null;
}

/** Badge state of the section; `none` = no link sent in the loaded window. */
export type SipOrderLinkStatus = 'none' | 'sent' | 'opened' | 'expired' | 'ordered';

function property(entry: TrackingEntry, name: string): string | null {
  const value = entry.properties?.find(p => p.name === name)?.value;
  return value == null || value === '' ? null : value;
}

function parseDate(value: string | null): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function parseId(value: string | null): number | null {
  if (value == null) return null;
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
}

/**
 * Maps tracking entries to history rows, grouped per link: links newest first (by their latest
 * activity), and within a link its `sent` row first, then its `opened` / `ordered` rows by time.
 * Sorting all rows by time alone would put an older link's late openings next to a newer link's
 * `sent` row and read as if they belonged to it. Entries of other functions, unknown `action`
 * values (e.g. a manual probe row) and entries with an unparseable `start` are dropped.
 *
 * @ticket PS-6586
 */
export function toSipOrderLinkRows(entries: readonly TrackingEntry[]): SipOrderLinkRow[] {
  const rows: SipOrderLinkRow[] = [];
  const openedByLink = new Map<string | null, SipOrderLinkRow>();

  for (const entry of entries) {
    if (entry.function !== TRACKING_FUNCTION_CONNECT_SIP_ORDER) continue;
    const at = parseDate(entry.start);
    if (!at) continue;
    const linkId = property(entry, 'linkId');
    const base: SipOrderLinkRow = {
      kind: 'sent', at, linkId, issuedBy: null, expiresAt: null, openCount: 0, result: null,
    };

    switch (property(entry, TRACKING_PROPERTY_ACTION)) {
      case SIP_ORDER_LINK_ACTION.sent:
        rows.push({
          ...base,
          issuedBy: parseId(property(entry, 'issuedBy')),
          expiresAt: parseDate(property(entry, 'expiresAt')),
        });
        break;
      case SIP_ORDER_LINK_ACTION.opened: {
        const opened = openedByLink.get(linkId);
        if (opened) {
          opened.openCount++;
          if (at > opened.at) opened.at = at;
        } else {
          const row: SipOrderLinkRow = { ...base, kind: 'opened', openCount: 1 };
          openedByLink.set(linkId, row);
          rows.push(row);
        }
        break;
      }
      case SIP_ORDER_LINK_ACTION.orderConfirmed:
        rows.push({ ...base, kind: 'ordered', result: property(entry, 'result') });
        break;
      default:
        break;
    }
  }

  const latestByLink = new Map<string | null, number>();
  for (const row of rows) {
    latestByLink.set(row.linkId, Math.max(latestByLink.get(row.linkId) ?? 0, row.at.getTime()));
  }
  const kindOrder: Record<SipOrderLinkRowKind, number> = { sent: 0, opened: 1, ordered: 2 };
  return rows.sort((a, b) =>
    (latestByLink.get(b.linkId) ?? 0) - (latestByLink.get(a.linkId) ?? 0)
    || String(a.linkId).localeCompare(String(b.linkId))
    || kindOrder[a.kind] - kindOrder[b.kind]
    || a.at.getTime() - b.at.getTime());
}

/**
 * The row the status badge belongs to, so it never lands on another link's row: the newest
 * `ordered` row when the customer ordered, else the newest `sent` row; null without either.
 * Rows are grouped by latest activity, so this is not necessarily the first row.
 *
 * @ticket PS-6598
 */
export function sipOrderLinkStatusRow(rows: readonly SipOrderLinkRow[]): SipOrderLinkRow | null {
  const newest = (kind: SipOrderLinkRowKind) => rows
    .filter(r => r.kind === kind)
    .reduce<SipOrderLinkRow | null>((best, r) => (!best || r.at > best.at ? r : best), null);
  return newest('ordered') ?? newest('sent');
}

/** Stable identity of a row for `@for … track`. */
export function sipOrderLinkRowKey(row: SipOrderLinkRow): string {
  return `${row.linkId ?? '-'}:${row.kind}:${row.at.getTime()}`;
}

/**
 * Status for the badge and the send button:
 * - `ordered` if any ORDER_CONFIRMED exists (any link — a second link answers ALREADY_EXISTS);
 * - otherwise the state of the newest LINK_SENT: `opened` if that link was opened, `expired`
 *   if it is past `expiresAt`, else `sent`;
 * - `none` without LINK_SENT.
 *
 * An opening of an older link does not mark a newer link as opened.
 *
 * @ticket PS-6586
 */
export function sipOrderLinkStatus(rows: readonly SipOrderLinkRow[], now: Date): SipOrderLinkStatus {
  if (rows.some(r => r.kind === 'ordered')) return 'ordered';
  const newestSent = rows
    .filter(r => r.kind === 'sent')
    .reduce<SipOrderLinkRow | null>((newest, r) => (!newest || r.at > newest.at ? r : newest), null);
  if (!newestSent) return 'none';
  if (rows.some(r => r.kind === 'opened' && r.linkId === newestSent.linkId)) return 'opened';
  if (newestSent.expiresAt && newestSent.expiresAt.getTime() <= now.getTime()) return 'expired';
  return 'sent';
}
